import React, { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import type { LoaderCallbacks, LoaderConfiguration, LoaderContext, LoaderStats } from 'hls.js';
import {
  getSegment,
  OfflineEpisode,
  getResumePosition,
  setResumePosition,
  clearResumePosition,
} from '../services/animeOfflineDB';
import { useBodyScrollLock } from '../utils/scrollLock';
import {
  X,
  AlertCircle,
  RotateCcw,
  RotateCw,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  RectangleHorizontal,
  Square,
  EyeOff,
  Loader2,
  Gauge,
  ZoomIn,
  Lock,
  LockOpen,
} from 'lucide-react';

interface OfflinePlayerModalProps {
  episode: OfflineEpisode;
  onClose: () => void;
  /** Called once the episode plays to (near) the end */
  onFinished?: (episode: OfflineEpisode) => void;
}

const SKIP_SECONDS = 5;
/** Two taps closer together than this count as a double tap (and keep a skip streak going) */
const DOUBLE_TAP_MS = 300;
const CONTROLS_HIDE_MS = 2500;
/** How long the unlock button stays up after a tap on the locked screen */
const UNLOCK_HINT_MS = 2500;
/** Resume points closer than this to the start aren't worth resuming from */
const RESUME_MIN_SECONDS = 10;
const RESUME_SAVE_EVERY = 5;

/** From here on the episode counts as watched: the last 90 s (credits) or the last 5% */
function watchedFrom(duration: number): number {
  return Math.min(duration - 90, duration * 0.95);
}

// Watermark cover: a blurred box the viewer can move and resize over the picture (in % of the picture).
// Watermarks differ per source, so the box is remembered per anime; an anime not set up yet
// starts from the box edited most recently.
interface CoverBox {
  on: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}
// Fits the AnimeKimi corner text
const DEFAULT_COVER: CoverBox = { on: true, x: 90.3, y: 2.4, w: 8.7, h: 5.2 };
const COVER_MIN = 2;
const COVER_STORE_KEY = 'offlinePlayer.cover.v2';
// Before the cover could be moved it only had a corner setting; 'off' is still honoured
const OLD_COVER_KEY = 'offlinePlayer.watermarkCover';

type CoverStore = { last?: CoverBox; byAnime?: Record<string, CoverBox> };
type CoverHandle = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';
type CoverDrag = 'move' | CoverHandle;

// Where each handle sits on the box (centred on its corner / side) and its resize cursor
const HANDLE_SPOTS: Record<CoverHandle, string> = {
  nw: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize',
  ne: 'top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize',
  sw: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize',
  se: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize',
  n: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize',
  s: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize',
  e: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
  w: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
};

// Zoom while placing the cover, so a small box can be grabbed on a phone: the picture is shown z times
// bigger and moved by (tx, ty) px inside the player
type CoverView = { z: number; tx: number; ty: number };
const NO_ZOOM: CoverView = { z: 1, tx: 0, ty: 0 };
const COVER_ZOOMS = [1, 2, 3, 4];
/** On a touch screen the editor opens zoomed in until the box is about this wide (px) */
const COVER_TOUCH_WIDTH = 180;

const isCoverBox = (c: any): c is CoverBox =>
  !!c && typeof c.on === 'boolean' && [c.x, c.y, c.w, c.h].every((n) => Number.isFinite(n));

function readCoverStore(): CoverStore {
  try {
    return JSON.parse(localStorage.getItem(COVER_STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function loadCover(animeId: string): CoverBox {
  const store = readCoverStore();
  const saved = store.byAnime?.[animeId] ?? store.last;
  if (isCoverBox(saved)) return saved;
  try {
    if (localStorage.getItem(OLD_COVER_KEY) === 'off') return { ...DEFAULT_COVER, on: false };
  } catch {}
  return DEFAULT_COVER;
}

function saveCover(animeId: string, c: CoverBox) {
  const store = readCoverStore();
  try {
    localStorage.setItem(
      COVER_STORE_KEY,
      JSON.stringify({ last: c, byAnime: { ...store.byAnime, [animeId]: c } })
    );
  } catch {
    // Storage blocked: the box just isn't remembered
  }
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/** New box after dragging the whole box, one side or one corner by (dx, dy) % of the picture */
function dragCover(start: CoverBox, mode: CoverDrag, dx: number, dy: number): CoverBox {
  if (mode === 'move') {
    return { ...start, x: clamp(start.x + dx, 0, 100 - start.w), y: clamp(start.y + dy, 0, 100 - start.h) };
  }
  // The sides the handle names follow the pointer ('nw' = top and left, 'e' = right only); the rest stay put
  let { x, y, w, h } = start;
  if (mode.includes('w')) {
    x = clamp(start.x + dx, 0, start.x + start.w - COVER_MIN);
    w = start.x + start.w - x;
  } else if (mode.includes('e')) {
    w = clamp(start.w + dx, COVER_MIN, 100 - start.x);
  }
  if (mode.includes('n')) {
    y = clamp(start.y + dy, 0, start.y + start.h - COVER_MIN);
    h = start.y + start.h - y;
  } else if (mode.includes('s')) {
    h = clamp(start.h + dy, COVER_MIN, 100 - start.y);
  }
  return { ...start, x, y, w, h };
}

// Desktop only: the medium ("theater") size makes the player much bigger without going fullscreen
const LARGE_KEY = 'offlinePlayer.large';

function loadLarge(): boolean {
  try {
    return localStorage.getItem(LARGE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveLarge(v: boolean) {
  try {
    localStorage.setItem(LARGE_KEY, v ? '1' : '0');
  } catch {
    // Storage blocked: the choice just isn't remembered
  }
}

// Playback speed, remembered across episodes
const SPEED_KEY = 'offlinePlayer.speed';
const SPEED_MIN = 0.25;
const SPEED_MAX = 3;
const SPEED_STEP = 0.05;
const SPEED_PRESETS = [1, 1.25, 1.5, 2, 3];

// Snap to the 0.05 grid; dividing by 20 (not multiplying by 0.05) keeps it free of float noise like 1.2500000000000002
const clampSpeed = (v: number) => Math.round(clamp(v, SPEED_MIN, SPEED_MAX) / SPEED_STEP) / (1 / SPEED_STEP);

function loadSpeed(): number {
  try {
    const v = Number(localStorage.getItem(SPEED_KEY));
    return Number.isFinite(v) && v > 0 ? clampSpeed(v) : 1;
  } catch {
    return 1;
  }
}

function saveSpeed(v: number) {
  try {
    localStorage.setItem(SPEED_KEY, String(v));
  } catch {
    // Storage blocked: the speed just isn't remembered
  }
}

/** 1 → "1.00x", 1.5 → "1.50x" */
const formatSpeed = (v: number) => `${v.toFixed(2)}x`;

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec % 60);
  const m = Math.floor(sec / 60) % 60;
  const h = Math.floor(sec / 3600);
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Where the picture actually sits inside the box (object-contain leaves black bars) */
function fitRect(boxW: number, boxH: number, vidW: number, vidH: number) {
  if (!boxW || !boxH || !vidW || !vidH) return { left: 0, top: 0, width: boxW, height: boxH };
  const scale = Math.min(boxW / vidW, boxH / vidH);
  const width = vidW * scale;
  const height = vidH * scale;
  return { left: (boxW - width) / 2, top: (boxH - height) / 2, width, height };
}

type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FsDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

// Screen lock also holds the orientation. Android can lock it (in fullscreen); iPhone can't, so there the
// player turns its picture back by however far the screen turned since locking
type Turn = 0 | 90 | 180 | -90;
const TURN_TRANSFORM: Record<Exclude<Turn, 0>, string> = {
  90: 'rotate(90deg) translateY(-100%)',
  [-90]: 'rotate(-90deg) translateX(-100%)',
  180: 'rotate(180deg) translate(-100%, -100%)',
};

/** How the screen is turned, in window.orientation terms: 0 upright, 90 turned left, -90 turned right, 180 */
function screenAngle(): number {
  const legacy = (window as any).orientation;
  if (typeof legacy === 'number') return legacy;
  const a = screen.orientation?.angle ?? 0;
  return a === 270 ? -90 : a;
}

function toTurn(deg: number): Turn {
  const d = (((deg % 360) + 540) % 360) - 180; // -180..179
  return d === -180 ? 180 : (d as Turn);
}

function fullscreenElement(): Element | null {
  const d = document as FsDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}
const PLAYLIST_URL = 'offline://episode/index.m3u8';
const SEGMENT_URL = /^offline:\/\/episode\/seg\/(\d+)$/;

function buildPlaylist(ep: OfflineEpisode): string {
  const target = Math.ceil(Math.max(1, ...ep.durations));
  const lines = [
    '#EXTM3U',
    '#EXT-X-VERSION:3',
    `#EXT-X-TARGETDURATION:${target}`,
    '#EXT-X-MEDIA-SEQUENCE:0',
    '#EXT-X-PLAYLIST-TYPE:VOD',
  ];
  ep.durations.forEach((d, i) => lines.push(`#EXTINF:${d.toFixed(6)},`, `offline://episode/seg/${i}`));
  lines.push('#EXT-X-ENDLIST');
  return lines.join('\n');
}

function newStats(): LoaderStats {
  return {
    aborted: false,
    loaded: 0,
    total: 0,
    retry: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { start: 0, first: 0, end: 0 },
    parsing: { start: 0, end: 0 },
    buffering: { start: 0, first: 0, end: 0 },
  };
}

/** hls.js loader that answers the playlist and segment requests from IndexedDB instead of the network */
function makeOfflineLoader(ep: OfflineEpisode) {
  const playlist = buildPlaylist(ep);
  return class OfflineLoader {
    context: LoaderContext | null = null;
    stats: LoaderStats = newStats();
    private aborted = false;

    constructor(_config: unknown) {}

    load(context: LoaderContext, _config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>) {
      this.context = context;
      this.stats = newStats();
      this.stats.loading.start = performance.now();
      const finish = (data: string | ArrayBuffer, size: number) => {
        if (this.aborted) return;
        const now = performance.now();
        this.stats.loading.first = now;
        this.stats.loading.end = now;
        this.stats.loaded = size;
        this.stats.total = size;
        callbacks.onSuccess({ url: context.url, data }, this.stats, context, null);
      };
      const fail = (text: string) => {
        if (!this.aborted) callbacks.onError({ code: 404, text }, context, null, this.stats);
      };

      // Always answer asynchronously like a network loader: hls.js registers the loader only after
      // load() returns, and a synchronous answer leaves the playlist unparsed (nothing ever plays)
      if (context.url === PLAYLIST_URL) {
        Promise.resolve().then(() => finish(playlist, playlist.length));
        return;
      }
      const m = context.url.match(SEGMENT_URL);
      if (!m) {
        fail('unknown offline url');
        return;
      }
      getSegment(ep.id, Number(m[1]))
        .then((blob) => (blob ? blob.arrayBuffer() : Promise.reject(new Error('missing segment'))))
        .then((buf) => finish(buf, buf.byteLength))
        .catch((e) => fail(e?.message || 'read failed'));
    }

    abort() {
      this.aborted = true;
      this.stats.aborted = true;
    }

    destroy() {
      this.aborted = true;
      this.context = null;
    }
  };
}

export const OfflinePlayerModal: React.FC<OfflinePlayerModalProps> = ({ episode, onClose, onFinished }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const finishedRef = useRef(false);
  const lastSavedRef = useRef(0);
  useBodyScrollLock(true);
  const [resumedFrom, setResumedFrom] = useState(0);

  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(true);
  // nativeFs: the browser's fullscreen; pseudoFs: fill the window instead (iPhone can't fullscreen a div)
  const [nativeFs, setNativeFs] = useState(false);
  const [pseudoFs, setPseudoFs] = useState(false);
  const [cover, setCover] = useState<CoverBox>(() => loadCover(episode.animeId));
  const [editingCover, setEditingCover] = useState(false);
  const coverDrag = useRef<{ mode: CoverDrag; x: number; y: number; start: CoverBox } | null>(null);
  const [large, setLarge] = useState(loadLarge);
  const [picture, setPicture] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [coverView, setCoverView] = useState<CoverView>(NO_ZOOM);
  // The picture as shown while zoomed (px in the player); the cover box and its drag math work on this
  const shownPicture =
    coverView.z === 1
      ? picture
      : {
          left: coverView.tx + coverView.z * picture.left,
          top: coverView.ty + coverView.z * picture.top,
          width: coverView.z * picture.width,
          height: coverView.z * picture.height,
        };
  const [skipHint, setSkipHint] = useState<{ side: 'back' | 'fwd'; total: number; key: number } | null>(null);
  const [speed, setSpeedState] = useState(loadSpeed);
  const [speedOpen, setSpeedOpen] = useState(false);
  const speedOpenRef = useRef(false);
  speedOpenRef.current = speedOpen;
  // Screen lock: taps on the picture do nothing (no pause / skip / controls) until unlocked
  const [locked, setLocked] = useState(false);
  const [lockAngle, setLockAngle] = useState(0);
  const [screenTurn, setScreenTurn] = useState({ angle: screenAngle(), w: window.innerWidth, h: window.innerHeight });
  const turned: Turn = locked ? toTurn(lockAngle - screenTurn.angle) : 0;
  const [unlockHint, setUnlockHint] = useState(false);
  const unlockHintTimer = useRef<number>();

  const hideTimer = useRef<number>();
  const tapTimer = useRef<number>();
  const hintTimer = useRef<number>();
  const lastTap = useRef<{ time: number; zone: 'back' | 'center' | 'fwd' } | null>(null);
  const fullscreen = nativeFs || pseudoFs;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hls: Hls | null = null;
    let cancelled = false;
    // hls.js is only needed here, so it loads with the player instead of with the app
    import('hls.js')
      .then(({ default: HlsLib }) => {
        if (cancelled) return;
        if (!HlsLib.isSupported()) {
          setError('เบราว์เซอร์นี้เล่นวิดีโอที่เก็บในแอปไม่ได้ (ต้องเป็น Chrome / Edge / Safari iOS 17.1 ขึ้นไป)');
          return;
        }
        // Carry on where the viewer left off, unless that was already in the credits
        const total = episode.durations.reduce((a, b) => a + b, 0);
        const saved = getResumePosition(episode.id);
        const resumeAt = saved >= RESUME_MIN_SECONDS && saved < watchedFrom(total) ? saved : 0;
        if (resumeAt) {
          setResumedFrom(resumeAt);
          lastSavedRef.current = resumeAt;
        }
        hls = new HlsLib({
          loader: makeOfflineLoader(episode) as any,
          startPosition: resumeAt || -1,
          maxBufferLength: 60,
          enableWorker: true,
        });
        hls.on(HlsLib.Events.ERROR, (_e, data) => {
          if (data.fatal) setError('เล่นวิดีโอไม่ได้ ไฟล์อาจเสียหาย ลองลบตอนนี้แล้วโหลดใหม่');
        });
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        hls.loadSource(PLAYLIST_URL);
        hls.attachMedia(video);
      })
      .catch(() => setError('โหลดตัวเล่นวิดีโอไม่สำเร็จ ลองเปิดใหม่อีกครั้ง'));
    // Also save when the app goes to the background (phones may kill it without closing the player)
    const onHide = () => document.visibilityState === 'hidden' && saveResume(video);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onHide);
      saveResume(video);
      hls?.destroy();
    };
  }, [episode.id]);

  // Hide the "resumed from" note after a few seconds
  useEffect(() => {
    if (!resumedFrom) return;
    const t = window.setTimeout(() => setResumedFrom(0), 6000);
    return () => window.clearTimeout(t);
  }, [resumedFrom]);

  useEffect(
    () => () => {
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(tapTimer.current);
      window.clearTimeout(hintTimer.current);
      window.clearTimeout(unlockHintTimer.current);
    },
    []
  );

  // Keep the watermark cover on the picture itself, not on the black bars around it
  const measure = () => {
    const box = boxRef.current;
    const v = videoRef.current;
    if (!box || !v) return;
    setPicture(fitRect(box.clientWidth, box.clientHeight, v.videoWidth, v.videoHeight));
  };
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [error]);

  useEffect(() => {
    const onChange = () => setNativeFs(!!fullscreenElement() && fullscreenElement() === boxRef.current);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  // Controls fade out while playing and come back on any movement / tap
  const showControls = () => {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      // Keep the controls up while the speed panel is open
      if (videoRef.current && !videoRef.current.paused && !speedOpenRef.current) setControlsVisible(false);
    }, CONTROLS_HIDE_MS);
  };
  useEffect(() => {
    if (!playing) {
      window.clearTimeout(hideTimer.current);
      setControlsVisible(true);
    } else {
      showControls();
    }
  }, [playing]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const skip = (delta: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = Math.min(Math.max(0, v.currentTime + delta), v.duration);
    setCurrent(v.currentTime);
  };

  const skipWithHint = (side: 'back' | 'fwd') => {
    skip(side === 'back' ? -SKIP_SECONDS : SKIP_SECONDS);
    setSkipHint((h) => ({
      side,
      total: h && h.side === side ? h.total + SKIP_SECONDS : SKIP_SECONDS,
      key: Date.now(),
    }));
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setSkipHint(null), 700);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.volume === 0) v.volume = 1;
  };

  const setSpeed = (next: number) => {
    const v = clampSpeed(next);
    setSpeedState(v);
    saveSpeed(v);
    const video = videoRef.current;
    if (video) {
      video.defaultPlaybackRate = v;
      video.playbackRate = v;
    }
  };

  // Loading the source resets the rate to the default, so apply the remembered speed once metadata is in
  const applySpeed = (video: HTMLVideoElement) => {
    video.defaultPlaybackRate = speed;
    video.playbackRate = speed;
  };

  const toggleFullscreen = async () => {
    if (pseudoFs) {
      setPseudoFs(false);
      return;
    }
    const d = document as FsDocument;
    if (fullscreenElement()) {
      await (d.exitFullscreen?.() ?? d.webkitExitFullscreen?.());
      return;
    }
    const box = boxRef.current as FsElement | null;
    if (!box) return;
    try {
      // iPhone reports element fullscreen as unavailable (only <video> can go fullscreen there)
      if (!(d.fullscreenEnabled ?? d.webkitFullscreenEnabled)) throw new Error('no element fullscreen');
      if (box.requestFullscreen) await box.requestFullscreen();
      else if (box.webkitRequestFullscreen) await box.webkitRequestFullscreen();
      else throw new Error('no element fullscreen');
      // Phones: turn sideways where the browser allows it
      await (screen.orientation as any)?.lock?.('landscape').catch(() => {});
    } catch {
      setPseudoFs(true);
    }
  };

  const toggleLarge = () => {
    setLarge((v) => {
      saveLarge(!v);
      return !v;
    });
  };

  const updateCover = (next: CoverBox) => {
    setCover(next);
    saveCover(episode.animeId, next);
  };

  const startCoverDrag = (mode: CoverDrag) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    coverDrag.current = { mode, x: e.clientX, y: e.clientY, start: cover };
  };
  const moveCoverDrag = (e: React.PointerEvent) => {
    const d = coverDrag.current;
    if (!d || !shownPicture.width || !shownPicture.height) return;
    const dx = ((e.clientX - d.x) / shownPicture.width) * 100;
    const dy = ((e.clientY - d.y) / shownPicture.height) * 100;
    setCover(dragCover(d.start, d.mode, dx, dy));
  };
  const endCoverDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (!coverDrag.current) return;
    coverDrag.current = null;
    updateCover(cover);
  };
  /** Zoom z times with the cover box in the middle of the player (z = 1: back to normal) */
  const zoomOnCover = (z: number, c: CoverBox = cover) => {
    const box = boxRef.current;
    if (!box || z <= 1 || !picture.width) {
      setCoverView(NO_ZOOM);
      return;
    }
    const cx = picture.left + (picture.width * (c.x + c.w / 2)) / 100;
    const cy = picture.top + (picture.height * (c.y + c.h / 2)) / 100;
    setCoverView({ z, tx: box.clientWidth / 2 - z * cx, ty: box.clientHeight / 2 - z * cy });
  };

  useEffect(() => {
    if (!editingCover) {
      setCoverView(NO_ZOOM);
      return;
    }
    // Phones: open zoomed in, so the box is big enough to grab
    if (window.matchMedia?.('(pointer: coarse)').matches && picture.width) {
      const fit = Math.round(COVER_TOUCH_WIDTH / ((picture.width * cover.w) / 100));
      zoomOnCover(clamp(fit, 1, COVER_ZOOMS[COVER_ZOOMS.length - 1]));
    }
  }, [editingCover]);

  // The player changed size (fullscreen, phone turned) while zoomed: centre on the box again
  useEffect(() => {
    if (coverView.z > 1) zoomOnCover(coverView.z);
  }, [picture.left, picture.top, picture.width, picture.height]);

  // Side handles only once the box is big enough on screen to leave room to grab the box itself
  const shownBoxW = (shownPicture.width * cover.w) / 100;
  const shownBoxH = (shownPicture.height * cover.h) / 100;
  const coverHandles: CoverHandle[] = [
    ...(shownBoxW >= 80 && shownBoxH >= 36 ? (['n', 's'] as const) : []),
    ...(shownBoxH >= 56 && shownBoxW >= 36 ? (['e', 'w'] as const) : []),
    // Corners last, so they win where they overlap a side handle
    'nw',
    'ne',
    'sw',
    'se',
  ];

  const coverDragHandlers = (mode: CoverDrag) => ({
    onPointerDown: startCoverDrag(mode),
    onPointerMove: moveCoverDrag,
    onPointerUp: endCoverDrag,
    onPointerCancel: endCoverDrag,
  });

  // Keyboard: ←/→ skip, space/k play, f fullscreen, m mute, </> speed, Esc leaves fill-window mode or closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      // Locked: no shortcuts either (Esc would close the player)
      if (locked) return;
      const onButton = (e.target as HTMLElement)?.tagName === 'BUTTON';
      let handled = true;
      switch (e.key) {
        case 'ArrowLeft':
          skipWithHint('back');
          break;
        case 'ArrowRight':
          skipWithHint('fwd');
          break;
        case ' ':
        case 'k':
          if (onButton && e.key === ' ') handled = false;
          else togglePlay();
          break;
        case 't':
          toggleLarge();
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'm':
          toggleMute();
          break;
        case '<':
          setSpeed(speed - 0.25);
          break;
        case '>':
          setSpeed(speed + 0.25);
          break;
        case 'Escape':
          if (speedOpen) setSpeedOpen(false);
          else if (editingCover) setEditingCover(false);
          else if (fullscreenElement()) handled = false;
          else if (pseudoFs) setPseudoFs(false);
          else onClose();
          break;
        default:
          handled = false;
      }
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
        showControls();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  // Taps on the picture: double tap left/right third skips 5 s (keep tapping to skip more),
  // double tap the middle toggles fullscreen; a single tap plays/pauses with a mouse,
  // or shows/hides the controls on a touch screen
  const flashUnlock = () => {
    setUnlockHint(true);
    window.clearTimeout(unlockHintTimer.current);
    unlockHintTimer.current = window.setTimeout(() => setUnlockHint(false), UNLOCK_HINT_MS);
  };

  useEffect(() => {
    // iOS reports the new angle a moment before the window size, so read both on each event
    const onTurn = () => setScreenTurn({ angle: screenAngle(), w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onTurn);
    window.addEventListener('orientationchange', onTurn);
    return () => {
      window.removeEventListener('resize', onTurn);
      window.removeEventListener('orientationchange', onTurn);
    };
  }, []);

  const lockScreen = () => {
    setLocked(true);
    setLockAngle(screenAngle());
    // Android (in fullscreen): have the browser hold the current orientation
    (screen.orientation as any)?.lock?.(screen.orientation.type).catch(() => {});
    setSpeedOpen(false);
    setEditingCover(false);
    window.clearTimeout(hideTimer.current);
    setControlsVisible(false);
    flashUnlock();
  };

  const unlockScreen = () => {
    setLocked(false);
    // Back to how fullscreen left it: sideways in fullscreen, free otherwise
    if (fullscreenElement()) (screen.orientation as any)?.lock?.('landscape').catch(() => {});
    else {
      try {
        screen.orientation?.unlock?.();
      } catch {}
    }
    setUnlockHint(false);
    window.clearTimeout(unlockHintTimer.current);
    showControls();
  };

  const handleTap = (e: React.PointerEvent<HTMLDivElement>) => {
    // Locked: a tap only brings up the unlock button
    if (locked) {
      flashUnlock();
      return;
    }
    // While the watermark box is being placed, stray taps shouldn't pause or skip
    if (editingCover) return;
    // A tap outside the open speed panel just closes it
    if (speedOpen) {
      setSpeedOpen(false);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const zone = x < 1 / 3 ? 'back' : x > 2 / 3 ? 'fwd' : 'center';
    const now = Date.now();
    const prev = lastTap.current;
    const isDouble = !!prev && now - prev.time < DOUBLE_TAP_MS && prev.zone === zone;
    lastTap.current = { time: now, zone };
    window.clearTimeout(tapTimer.current);

    if (isDouble) {
      if (zone === 'center') {
        lastTap.current = null;
        toggleFullscreen();
      } else {
        skipWithHint(zone);
      }
      return;
    }
    const isMouse = e.pointerType === 'mouse';
    tapTimer.current = window.setTimeout(() => {
      if (isMouse) {
        togglePlay();
        showControls();
      } else if (controlsVisible && videoRef.current && !videoRef.current.paused) {
        window.clearTimeout(hideTimer.current);
        setControlsVisible(false);
      } else {
        showControls();
      }
    }, DOUBLE_TAP_MS);
  };

  // Takes the element because on unmount videoRef is already cleared when effect cleanups run
  function saveResume(v: HTMLVideoElement | null = videoRef.current) {
    if (!v || !v.duration) return;
    const t = v.currentTime;
    if (t >= watchedFrom(v.duration)) clearResumePosition(episode.id);
    else if (t >= RESUME_MIN_SECONDS) setResumePosition(episode.id, t);
    lastSavedRef.current = t;
  }

  const restartFromBeginning = () => {
    const v = videoRef.current;
    if (v) v.currentTime = 0;
    clearResumePosition(episode.id);
    lastSavedRef.current = 0;
    setResumedFrom(0);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrent(v.currentTime);
    if (!v.duration) return;
    if (Math.abs(v.currentTime - lastSavedRef.current) >= RESUME_SAVE_EVERY) saveResume(v);
    if (!finishedRef.current && v.currentTime >= watchedFrom(v.duration)) {
      finishedRef.current = true;
      clearResumePosition(episode.id);
      onFinished?.(episode);
    }
  };

  const btn = 'p-1.5 sm:p-2 rounded-lg text-white/90 hover:text-white hover:bg-white/15 shrink-0';
  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div
      onClick={locked ? undefined : onClose}
      className="fixed inset-0 bg-black/95 z-[70] flex items-center justify-center safe-area-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        // Medium size: about 72% of a big screen (at least 80rem), never wider than the window
        // or so tall that the 16:9 picture plus the title stops fitting its height
        style={large ? { maxWidth: 'min(max(72vw, 80rem), 94vw, calc((100vh - 7rem) * 16 / 9 + 2rem))' } : undefined}
        className={`w-full flex flex-col gap-2 p-2 sm:p-4 ${large ? '' : 'max-w-5xl'}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm sm:text-base font-bold text-white truncate">{episode.animeTitle}</div>
            <div className="text-xs text-slate-400 truncate">
              ตอนที่ {episode.number} · {episode.audio} · ดูแบบออฟไลน์
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-300 hover:text-white rounded-xl bg-slate-800/80 hover:bg-slate-700 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error ? (
          <div className="aspect-video w-full rounded-xl bg-slate-900 border border-rose-500/40 flex items-center justify-center p-6 text-center text-sm text-rose-300 gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : (
          <>
          {/* Behind the turned-back player */}
          {turned !== 0 && <div className="fixed inset-0 z-[79] bg-black" />}
          <div
            ref={boxRef}
            onPointerMove={(e) => e.pointerType === 'mouse' && showControls()}
            style={
              turned
                ? {
                    top: 0,
                    left: 0,
                    right: 'auto',
                    bottom: 'auto',
                    width: turned === 180 ? screenTurn.w : screenTurn.h,
                    height: turned === 180 ? screenTurn.h : screenTurn.w,
                    transformOrigin: 'top left',
                    transform: TURN_TRANSFORM[turned],
                  }
                : undefined
            }
            className={`bg-black overflow-hidden select-none ${
              pseudoFs || turned
                ? 'fixed inset-0 z-[80]'
                : nativeFs
                  ? 'relative w-full h-full'
                  : `relative w-full aspect-video rounded-xl ${large ? 'max-h-[calc(100vh-7rem)]' : 'max-h-[80vh]'}`
            } ${controlsVisible ? '' : 'cursor-none'}`}
          >
            <video
              ref={videoRef}
              playsInline
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={(e) => {
                setDuration(e.currentTarget.duration);
                applySpeed(e.currentTarget);
                measure();
              }}
              onDurationChange={(e) => setDuration(e.currentTarget.duration)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onWaiting={() => setWaiting(true)}
              onPlaying={() => setWaiting(false)}
              onCanPlay={() => setWaiting(false)}
              onVolumeChange={(e) => {
                setMuted(e.currentTarget.muted);
                setVolume(e.currentTarget.volume);
              }}
              // Stay in step if the browser changes the rate itself (e.g. its own media controls)
              onRateChange={(e) => setSpeedState(e.currentTarget.playbackRate)}
              style={
                coverView.z > 1
                  ? {
                      transform: `translate(${coverView.tx}px, ${coverView.ty}px) scale(${coverView.z})`,
                      transformOrigin: '0 0',
                    }
                  : undefined
              }
              className="absolute inset-0 w-full h-full object-contain transition-transform duration-200"
            />

            {/* Tap layer (sits under the control bar) */}
            <div className="absolute inset-0 touch-manipulation" onPointerUp={handleTap} />

            {/* Watermark cover, placed on the picture rather than the whole box */}
            {(cover.on || editingCover) && picture.width > 0 && (
              // While editing it sits above the control bar so a box near the bottom can still be grabbed.
              // It follows the zoomed picture by position and size (not a CSS scale) so the handles keep their size.
              <div
                className={`absolute pointer-events-none transition-[left,top,width,height] duration-200 ${
                  editingCover ? 'z-10' : ''
                }`}
                style={shownPicture}
              >
                <div
                  {...(editingCover ? coverDragHandlers('move') : {})}
                  className={`absolute rounded-sm ${cover.on ? 'bg-black/40 backdrop-blur-xl' : ''} ${
                    editingCover
                      ? 'pointer-events-auto cursor-move touch-none outline-dashed outline-2 outline-sky-400'
                      : ''
                  }`}
                  style={{ left: `${cover.x}%`, top: `${cover.y}%`, width: `${cover.w}%`, height: `${cover.h}%` }}
                >
                  {editingCover &&
                    coverHandles.map((handle) => {
                      const corner = handle.length === 2;
                      const across = handle === 'n' || handle === 's';
                      return (
                        // A bigger see-through area around the dot / bar, so it's easy to hit with a finger
                        <div
                          key={handle}
                          {...coverDragHandlers(handle)}
                          className={`absolute grid place-items-center touch-none ${HANDLE_SPOTS[handle]} ${
                            corner ? 'w-8 h-8' : across ? 'w-10 h-6' : 'w-6 h-10'
                          }`}
                        >
                          <div
                            className={`bg-sky-400 border-white ${
                              corner
                                ? 'w-4 h-4 rounded-full border-2'
                                : `rounded-full border ${across ? 'w-6 h-2' : 'w-2 h-6'}`
                            }`}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {editingCover && (
              <div className="absolute top-2 inset-x-2 z-20 flex justify-center pointer-events-none">
                <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-black/80 text-white text-xs sm:text-sm">
                  <span className="text-white/80 mr-1">ลากกรอบเพื่อย้าย · ลากจุดที่มุมหรือขอบเพื่อปรับขนาด</span>
                  <button
                    type="button"
                    onClick={() =>
                      zoomOnCover(COVER_ZOOMS[(COVER_ZOOMS.indexOf(coverView.z) + 1) % COVER_ZOOMS.length])
                    }
                    className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 flex items-center gap-1"
                    title="ซูมเข้าที่กรอบ (กดซ้ำเพื่อเปลี่ยนระดับ)"
                  >
                    <ZoomIn className="w-4 h-4" />
                    ซูม {coverView.z}x
                  </button>
                  <button
                    type="button"
                    onClick={() => updateCover({ ...cover, on: !cover.on })}
                    className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25"
                  >
                    {cover.on ? 'ปิดการบัง' : 'เปิดการบัง'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const c = { ...DEFAULT_COVER, on: true };
                      updateCover(c);
                      if (coverView.z > 1) zoomOnCover(coverView.z, c);
                    }}
                    className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25"
                  >
                    ค่าเริ่มต้น
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingCover(false)}
                    className="px-2.5 py-1 rounded-lg bg-sky-500 hover:bg-sky-400 font-semibold"
                  >
                    เสร็จ
                  </button>
                </div>
              </div>
            )}

            {waiting && !error && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="w-10 h-10 text-white/80 animate-spin" />
              </div>
            )}

            {resumedFrom > 0 && !locked && (
              <div className="absolute left-3 bottom-20 flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-black/70 text-white text-xs sm:text-sm">
                <span>เล่นต่อจาก {formatTime(resumedFrom)}</span>
                <button
                  type="button"
                  onClick={restartFromBeginning}
                  className="px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 font-semibold"
                >
                  เริ่มใหม่
                </button>
              </div>
            )}

            {skipHint && (
              <div
                key={skipHint.key}
                className={`absolute top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1.5 px-4 py-2 rounded-full bg-black/60 text-white text-sm font-bold ${
                  skipHint.side === 'back' ? 'left-[12%]' : 'right-[12%]'
                }`}
              >
                {skipHint.side === 'back' ? <RotateCcw className="w-4 h-4" /> : null}
                {skipHint.side === 'back' ? '−' : '+'}
                {skipHint.total} วิ
                {skipHint.side === 'fwd' ? <RotateCw className="w-4 h-4" /> : null}
              </div>
            )}

            {speedOpen && (
              <div
                onPointerMove={showControls}
                className="absolute right-2 sm:right-3 bottom-[4.5rem] z-20 w-[min(20rem,calc(100%-1rem))] p-3 rounded-xl bg-black/85 backdrop-blur text-white shadow-xl"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">ความเร็วในการเล่น</span>
                  <button
                    type="button"
                    onClick={() => setSpeedOpen(false)}
                    className="p-1 -m-1 rounded-lg text-white/70 hover:text-white hover:bg-white/15"
                    aria-label="ปิด"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-center text-lg font-bold tabular-nums my-1">{formatSpeed(speed)}</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSpeed(speed - SPEED_STEP)}
                    disabled={speed <= SPEED_MIN}
                    className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-40 font-bold shrink-0"
                    aria-label="ช้าลง"
                  >
                    −
                  </button>
                  <input
                    type="range"
                    min={SPEED_MIN}
                    max={SPEED_MAX}
                    step={SPEED_STEP}
                    value={speed}
                    onChange={(e) => setSpeed(Number(e.target.value))}
                    aria-label="ความเร็วในการเล่น"
                    className="flex-1 min-w-0 h-1 cursor-pointer accent-white"
                  />
                  <button
                    type="button"
                    onClick={() => setSpeed(speed + SPEED_STEP)}
                    disabled={speed >= SPEED_MAX}
                    className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-40 font-bold shrink-0"
                    aria-label="เร็วขึ้น"
                  >
                    +
                  </button>
                </div>
                <div className="flex gap-1.5 mt-3">
                  {SPEED_PRESETS.map((p) => (
                    <div key={p} className="flex-1 flex flex-col items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => setSpeed(p)}
                        className={`w-full py-1.5 rounded-full text-xs font-semibold tabular-nums ${
                          Math.abs(speed - p) < 0.001 ? 'bg-sky-500' : 'bg-white/15 hover:bg-white/25'
                        }`}
                      >
                        {Number.isInteger(p) ? p.toFixed(1) : p}
                      </button>
                      {p === 1 && <span className="text-[10px] text-white/60">ปกติ</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {locked && unlockHint && (
              // At the top: subtitles sit at the bottom
              <div className="absolute inset-x-0 top-4 z-20 flex justify-center pointer-events-none">
                <button
                  type="button"
                  onClick={unlockScreen}
                  className="pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/75 hover:bg-black/90 text-white text-sm font-semibold"
                >
                  <LockOpen className="w-5 h-5" />
                  แตะเพื่อปลดล็อคหน้าจอ
                </button>
              </div>
            )}

            {/* Control bar */}
            <div
              onPointerMove={showControls}
              className={`absolute inset-x-0 bottom-0 px-2 sm:px-3 pb-1.5 pt-8 bg-gradient-to-t from-black/85 via-black/50 to-transparent transition-opacity duration-200 ${
                controlsVisible && !locked ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <input
                type="range"
                min={0}
                max={duration || 0}
                step="any"
                value={Math.min(current, duration || 0)}
                onChange={(e) => {
                  const v = videoRef.current;
                  if (!v) return;
                  v.currentTime = Number(e.target.value);
                  setCurrent(v.currentTime);
                  showControls();
                }}
                aria-label="ตำแหน่งวิดีโอ"
                className="w-full h-1.5 cursor-pointer accent-sky-500"
                style={{
                  background: `linear-gradient(to right, rgb(14 165 233) ${progress}%, rgba(255,255,255,0.25) ${progress}%)`,
                }}
              />
              <div className="flex items-center gap-0.5 sm:gap-1 text-white">
                <button type="button" onClick={togglePlay} className={btn} aria-label={playing ? 'หยุด' : 'เล่น'}>
                  {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button
                  type="button"
                  onClick={() => skipWithHint('back')}
                  className={btn}
                  aria-label={`ถอยหลัง ${SKIP_SECONDS} วิ`}
                  title={`ถอยหลัง ${SKIP_SECONDS} วิ (←)`}
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => skipWithHint('fwd')}
                  className={btn}
                  aria-label={`ไปหน้า ${SKIP_SECONDS} วิ`}
                  title={`ไปหน้า ${SKIP_SECONDS} วิ (→)`}
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <button type="button" onClick={toggleMute} className={btn} aria-label={muted ? 'เปิดเสียง' : 'ปิดเสียง'}>
                  {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    const v = videoRef.current;
                    if (!v) return;
                    v.volume = Number(e.target.value);
                    v.muted = v.volume === 0;
                  }}
                  aria-label="ระดับเสียง"
                  className="hidden sm:block w-20 h-1 cursor-pointer accent-white"
                />
                <span className="text-xs sm:text-sm tabular-nums px-1 sm:px-1.5 whitespace-nowrap">
                  {formatTime(current)} / {formatTime(duration)}
                </span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => setSpeedOpen((v) => !v)}
                  className={`${btn} flex items-center gap-1 text-xs font-semibold tabular-nums ${
                    speedOpen ? 'bg-white/20' : ''
                  } ${speed === 1 ? '' : 'text-sky-300'}`}
                  aria-label="ความเร็วในการเล่น"
                  title="ความเร็วในการเล่น (< / >)"
                >
                  <Gauge className="w-4 h-4" />
                  {speed === 1 ? '1x' : `${+speed.toFixed(2)}x`}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingCover((v) => !v)}
                  className={`${btn} flex items-center gap-1 text-xs ${cover.on ? '' : 'opacity-60'} ${
                    editingCover ? 'bg-white/20' : ''
                  }`}
                  title="บังลายน้ำ: กดเพื่อย้าย / ปรับขนาดกรอบ"
                >
                  <EyeOff className="w-4 h-4" />
                  <span className="hidden sm:inline">บังลายน้ำ:</span>
                  {editingCover ? 'กำลังปรับ' : cover.on ? 'เปิด' : 'ปิด'}
                </button>
                {!fullscreen && (
                  <button
                    type="button"
                    onClick={toggleLarge}
                    className={`${btn} hidden sm:block`}
                    aria-label={large ? 'ขนาดปกติ' : 'ขนาดกลาง'}
                    title={`${large ? 'ขนาดปกติ' : 'ขนาดกลาง'} (t)`}
                  >
                    {large ? <Square className="w-5 h-5" /> : <RectangleHorizontal className="w-5 h-5" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={lockScreen}
                  className={`${btn} flex items-center gap-1 text-xs`}
                  aria-label="ล็อคหน้าจอ"
                  title="ล็อคหน้าจอ: กันแตะโดนตอนดู"
                >
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">ล็อค</span>
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className={btn}
                  aria-label={fullscreen ? 'ออกจากเต็มจอ' : 'เต็มจอ'}
                  title={`${fullscreen ? 'ออกจากเต็มจอ' : 'เต็มจอ'} (f)`}
                >
                  {fullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};
